/**
 * v1.14.61.1.0 — Wizard v2 "Bulk per garanzia" / GaranziaTableStep.jsx
 *
 * Componente generico riusabile per i 6 step garanzia tabellari del
 * Wizard v2 (Incendio / Furto / RCF / Atmosferici / Catastrofali /
 * Altre). Renderizza una tabella immobili × campi con checkbox "Includi"
 * + sezione sub-coperture (globali o per-immobile).
 *
 * Props:
 * - title, subtitle, icon: header
 * - immobili: [{ ubicazione_id, indirizzo, comune, provincia, settore }]
 * - columns: [{ key, label, type: "number"|"select"|"toggle", options?,
 *               readOnly?, computed? }]
 *   → colonne dati per-immobile (incluso `includi` implicito a sinistra)
 * - selectedIds: string[]
 * - perImmobile: { [uid]: { [key]: value } }
 * - onSelectionChange(newIds)
 * - onValueChange(uid, key, value)
 * - subCoperture: [{
 *     code, label, type: "toggle"|"value_eur"|"limit_select",
 *     options?, sectorBound?, manualInput?, hint? }]
 * - subCopMode: "global" | "per_immobile"
 * - subCopValues: { [code]: {...} | { [uid]: {...} } }
 * - onSubCopChange(code, payload, uid?)
 * - rdWarning?: string (warning rosso obbligatorio per Catastrofali)
 */
import React, { useMemo } from "react";
import { Info, AlertTriangle } from "lucide-react";
import InfoTooltip from "./InfoTooltip";

const PALETTE = {
  navy: "#0B2545",
  navyDark: "#06182E",
  gold: "#C9A961",
  goldSoft: "#E2C988",
  cream: "#F8F4ED",
  ink: "#1A1A1A",
  text: "#3A3A3A",
  muted: "#6E6E6E",
  border: "#E5DFD4",
  rowAlt: "#FAF8F3",
  red: "#B23B3B",
};

const numFmt = (n) =>
  Number.isFinite(Number(n)) ? Number(n).toLocaleString("it-IT") : "";

/**
 * BigCheckbox: checkbox quadrato 24×24 (28 mobile) con bordo navy
 * visibile in stato OFF e sfondo oro + ✓ in stato ON. Touch target 48×48
 * tramite padding click area circostante.
 */
function BigCheckbox({ checked, onChange, testid, disabled, size = 24 }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={!!checked}
      data-testid={testid}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      className="flex items-center justify-center transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-1"
      style={{
        width: size, height: size, minWidth: size, minHeight: size,
        borderRadius: 6,
        border: `2px solid ${checked ? PALETTE.gold : PALETTE.navy}`,
        background: checked ? PALETTE.gold : "#FFF",
        color: checked ? PALETTE.navyDark : "transparent",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        flexShrink: 0,
        boxShadow: checked
          ? "0 1px 3px rgba(11,37,69,0.18)"
          : "0 0 0 1px rgba(11,37,69,0.04)",
      }}
    >
      <svg
        width={size - 8} height={size - 8} viewBox="0 0 20 20"
        fill="none" style={{ display: checked ? "block" : "none" }}
        aria-hidden="true"
      >
        <path d="M4 10.5L8.5 15L16 6"
              stroke={PALETTE.navyDark} strokeWidth="3.2"
              strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

function ImmobileRow({
  immobile, included, perImmobileValues, columns,
  onToggleInclude, onValueChange,
}) {
  const uid = immobile.ubicazione_id;
  const settore = immobile.settore || immobile.settore_tariffa;
  const vals = perImmobileValues[uid] || {};
  return (
    <tr
      data-testid={`row-immobile-${uid}`}
      style={{
        background: included ? "#FFF" : PALETTE.rowAlt,
        opacity: included ? 1 : 0.55,
      }}
    >
      <td className="px-3 py-3 align-top">
        <div
          className="p-1 -m-1 inline-flex items-center justify-center"
          style={{ minWidth: 40, minHeight: 40 }}
        >
          <BigCheckbox
            checked={included}
            onChange={(v) => onToggleInclude(uid, v)}
            testid={`toggle-include-${uid}`}
          />
        </div>
      </td>
      <td className="px-2 py-2 align-top text-sm" style={{ minWidth: 180 }}>
        <div className="font-medium" style={{ color: PALETTE.ink }}>
          {uid}
        </div>
        <div className="text-xs" style={{ color: PALETTE.muted }}>
          {immobile.indirizzo || ""}
          {immobile.comune ? `, ${immobile.comune}` : ""}
          {immobile.provincia ? ` (${immobile.provincia})` : ""}
        </div>
        {settore && (
          <div
            className="text-xs mt-1 inline-block px-1.5 py-0.5 rounded"
            style={{
              background: PALETTE.cream, color: PALETTE.navy,
              fontSize: 10,
            }}
          >
            {settore}
          </div>
        )}
      </td>
      {columns.map((col) => {
        const v = vals[col.key];
        const disabled = !included || col.readOnly;
        return (
          <td
            key={col.key}
            className="px-2 py-2 align-top"
            style={{ minWidth: col.minWidth || 140 }}
          >
            {col.type === "number" && (
              <input
                type="number"
                data-testid={`field-${col.key}-${uid}`}
                value={v ?? ""}
                disabled={disabled}
                onChange={(e) =>
                  onValueChange(uid, col.key,
                    e.target.value === "" ? null
                      : Number(e.target.value))}
                placeholder={col.placeholder || "0"}
                min="0"
                step={col.step || "1"}
                className="w-full px-2 py-1 text-sm rounded border"
                style={{
                  borderColor: PALETTE.border,
                  background: disabled ? PALETTE.cream : "#FFF",
                }}
              />
            )}
            {col.type === "select" && (
              <select
                data-testid={`field-${col.key}-${uid}`}
                value={v ?? ""}
                disabled={disabled}
                onChange={(e) =>
                  onValueChange(uid, col.key, e.target.value)}
                className="w-full px-2 py-1 text-sm rounded border"
                style={{
                  borderColor: PALETTE.border,
                  background: disabled ? PALETTE.cream : "#FFF",
                }}
              >
                <option value="">—</option>
                {(col.options || []).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
            {col.type === "toggle" && (
              <div
                className="p-1 -m-1 inline-flex items-center justify-center"
                style={{ minWidth: 36, minHeight: 36 }}
              >
                <BigCheckbox
                  checked={!!v}
                  onChange={(nv) => onValueChange(uid, col.key, nv)}
                  testid={`field-${col.key}-${uid}`}
                  disabled={disabled}
                  size={22}
                />
              </div>
            )}
            {col.computed && (
              <div className="text-sm" style={{ color: PALETTE.muted }}>
                {col.computed(immobile, vals)}
              </div>
            )}
          </td>
        );
      })}
    </tr>
  );
}

function SubCoperturaRow({
  sub, subCopMode, value, selectedIds, immobili, onChange,
}) {
  // Global mode: { attiva, valore_eur?, limite_eur? }
  const cfg = value || {};
  const isToggleType = sub.type === "toggle";
  const isValueType = sub.type === "value_eur";
  const isLimitType = sub.type === "limit_select";

  if (subCopMode === "global") {
    const toggleActive = () =>
      onChange(sub.code, { ...cfg, attiva: !cfg.attiva });
    return (
      <tr
        data-testid={`sub-row-${sub.code}`}
        className="transition-colors hover:bg-amber-50/30"
        style={{ borderTop: `1px solid ${PALETTE.border}` }}
      >
        <td className="px-3 py-3 align-top text-sm" style={{ minWidth: 280 }}>
          <div className="flex items-start gap-3">
            <div
              className="cursor-pointer p-1 -m-1 inline-flex items-center justify-center"
              style={{ minWidth: 40, minHeight: 40 }}
              onClick={toggleActive}
            >
              <BigCheckbox
                checked={!!cfg.attiva}
                onChange={() => toggleActive()}
                testid={`sub-toggle-${sub.code}`}
              />
            </div>
            <div
              className="flex-1 cursor-pointer select-none"
              onClick={toggleActive}
            >
              <div className="flex items-center flex-wrap gap-x-1">
                <span style={{ color: PALETTE.ink, fontWeight: 500 }}>
                  {sub.label}
                </span>
                {sub.tooltip && (
                  <span onClick={(e) => e.stopPropagation()}>
                    <InfoTooltip
                      title={sub.tooltip.title || sub.label}
                      body={sub.tooltip.body}
                      ref={sub.tooltip.ref}
                      testid={`tooltip-sub-${sub.code}`}
                    />
                  </span>
                )}
                {sub.sectorBound && (
                  <span
                    className="ml-2 text-xs px-1.5 py-0.5 rounded"
                    style={{
                      background: "#FFF4E6", color: "#A05C00",
                      fontSize: 10,
                    }}
                  >
                    Solo {sub.sectorBound}
                  </span>
                )}
                {sub.manualInput && (
                  <span
                    className="ml-2 text-xs"
                    style={{ color: PALETTE.red, fontSize: 10 }}
                  >
                    ⚠ Richiede input manuale
                  </span>
                )}
              </div>
              {sub.hint && (
                <div className="text-xs mt-0.5"
                      style={{ color: PALETTE.muted }}>
                  {sub.hint}
                </div>
              )}
            </div>
          </div>
        </td>
        <td className="px-2 py-2 align-top">
          {isValueType && cfg.attiva && (
            <div className="flex flex-col gap-1">
              <input
                type="number"
                data-testid={`sub-value-${sub.code}`}
                value={cfg.valore_eur ?? ""}
                onChange={(e) =>
                  onChange(sub.code, {
                    ...cfg,
                    valore_eur: e.target.value === ""
                      ? 0 : Number(e.target.value),
                  })}
                placeholder={sub.placeholder || "Valore €"}
                min="0"
                className="w-full px-2 py-1 text-sm rounded border tabular-nums"
                style={{ borderColor: PALETTE.border, maxWidth: 200 }}
              />
              {sub.rate_permille && cfg.valore_eur > 0 && (
                <span className="text-[10px] tabular-nums"
                       style={{ color: PALETTE.muted }}
                       data-testid={`sub-preview-${sub.code}`}>
                  {Number(cfg.valore_eur).toLocaleString("it-IT")}{" "}
                  € × {sub.rate_permille}‰ ={" "}
                  <strong style={{ color: PALETTE.navy }}>
                    {((cfg.valore_eur * sub.rate_permille) / 1000)
                      .toLocaleString("it-IT", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })} €
                  </strong>
                </span>
              )}
              {sub.manualRateInfo && cfg.valore_eur > 0 && (
                <span className="text-[10px] italic"
                       style={{ color: PALETTE.muted }}>
                  ↳ {sub.manualRateInfo}
                </span>
              )}
            </div>
          )}
          {isLimitType && cfg.attiva && (
            <select
              data-testid={`sub-limit-${sub.code}`}
              value={cfg.limite_eur ?? ""}
              onChange={(e) =>
                onChange(sub.code, {
                  ...cfg, limite_eur: Number(e.target.value),
                })}
              className="px-2 py-1 text-sm rounded border"
              style={{ borderColor: PALETTE.border, minWidth: 140 }}
            >
              <option value="">— scegli limite —</option>
              {(sub.options || []).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}
          {isToggleType && (
            <span className="text-xs" style={{ color: PALETTE.muted }}>
              {cfg.attiva ? "Attiva su tutti gli immobili inclusi" : ""}
            </span>
          )}
        </td>
      </tr>
    );
  }

  // per_immobile mode: cfg = { [uid]: { attiva, ... } }
  return (
    <>
      <tr data-testid={`sub-row-${sub.code}`}>
        <td
          colSpan={2}
          className="px-2 py-2 align-top text-sm"
          style={{
            minWidth: 280, background: PALETTE.cream,
            borderTop: `1px solid ${PALETTE.border}`,
          }}
        >
          <div style={{ color: PALETTE.ink, fontWeight: 500 }}>
            {sub.label}
            {sub.tooltip && (
              <InfoTooltip
                title={sub.tooltip.title || sub.label}
                body={sub.tooltip.body}
                ref={sub.tooltip.ref}
                testid={`tooltip-sub-${sub.code}`}
              />
            )}
            {sub.sectorBound && (
              <span
                className="ml-2 text-xs px-1.5 py-0.5 rounded"
                style={{
                  background: "#FFF4E6", color: "#A05C00", fontSize: 10,
                }}
              >
                Solo {sub.sectorBound}
              </span>
            )}
            {sub.manualInput && (
              <span
                className="ml-2 text-xs"
                style={{ color: PALETTE.red, fontSize: 10 }}
              >
                ⚠ Richiede input manuale
              </span>
            )}
          </div>
          {sub.hint && (
            <div className="text-xs mt-0.5" style={{ color: PALETTE.muted }}>
              {sub.hint}
            </div>
          )}
        </td>
      </tr>
      {selectedIds.map((uid) => {
        const ucfg = (cfg[uid] || {});
        const imm = immobili.find((m) => m.ubicazione_id === uid);
        if (!imm) return null;
        return (
          <tr
            key={`${sub.code}-${uid}`}
            data-testid={`sub-row-${sub.code}-${uid}`}
            className="transition-colors hover:bg-amber-50/20"
          >
            <td className="px-2 py-2 pl-6 text-xs"
                 style={{ color: PALETTE.text }}>
              <div
                className="flex items-center gap-2 cursor-pointer select-none"
                onClick={() => onChange(sub.code, {
                  ...ucfg, attiva: !ucfg.attiva }, uid)}
              >
                <div
                  className="p-1 -m-1 inline-flex items-center justify-center"
                  style={{ minWidth: 32, minHeight: 32 }}
                >
                  <BigCheckbox
                    checked={!!ucfg.attiva}
                    onChange={() => onChange(sub.code, {
                      ...ucfg, attiva: !ucfg.attiva }, uid)}
                    testid={`sub-toggle-${sub.code}-${uid}`}
                    size={20}
                  />
                </div>
                <span className="font-medium"
                       style={{ color: PALETTE.navyDark }}>
                  {uid}
                </span>
              </div>
            </td>
            <td className="px-2 py-1.5">
              {isValueType && ucfg.attiva && (
                <div className="flex flex-col gap-1">
                  <input
                    type="number"
                    data-testid={`sub-value-${sub.code}-${uid}`}
                    value={ucfg.valore_eur ?? ""}
                    onChange={(e) => onChange(sub.code, {
                      ...ucfg,
                      valore_eur: e.target.value === ""
                        ? 0 : Number(e.target.value),
                    }, uid)}
                    placeholder={sub.placeholder || "Valore €"}
                    min="0"
                    className="w-full px-2 py-1 text-xs rounded border tabular-nums"
                    style={{ borderColor: PALETTE.border, maxWidth: 180 }}
                  />
                  {sub.rate_permille && ucfg.valore_eur > 0 && (
                    <span className="text-[10px] tabular-nums"
                           style={{ color: PALETTE.muted }}>
                      × {sub.rate_permille}‰ ={" "}
                      <strong style={{ color: PALETTE.navy }}>
                        {((ucfg.valore_eur * sub.rate_permille) / 1000)
                          .toLocaleString("it-IT", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} €
                      </strong>
                    </span>
                  )}
                </div>
              )}
              {isLimitType && ucfg.attiva && (
                <select
                  data-testid={`sub-limit-${sub.code}-${uid}`}
                  value={ucfg.limite_eur ?? ""}
                  onChange={(e) => onChange(sub.code, {
                    ...ucfg, limite_eur: Number(e.target.value),
                  }, uid)}
                  className="px-2 py-1 text-xs rounded border"
                  style={{ borderColor: PALETTE.border, minWidth: 130 }}
                >
                  <option value="">— limite —</option>
                  {(sub.options || []).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              )}
              {sub.extraFields && ucfg.attiva && sub.extraFields(
                ucfg, (patch) => onChange(sub.code, { ...ucfg, ...patch }, uid))}
            </td>
          </tr>
        );
      })}
    </>
  );
}

export default function GaranziaTableStep({
  title, subtitle, icon: Icon,
  immobili = [],
  columns = [],
  selectedIds = [],
  perImmobile = {},
  onSelectionChange,
  onValueChange,
  subCoperture = [],
  subCopMode = "global",
  subCopValues = {},
  onSubCopChange,
  rdWarning,
  selectAllLabel = "Seleziona tutti",
  emptyHint,
}) {
  const allSelected = useMemo(
    () => immobili.length > 0 && selectedIds.length === immobili.length,
    [immobili, selectedIds],
  );
  const someSelected = selectedIds.length > 0;

  const toggleAll = () => {
    if (allSelected) onSelectionChange([]);
    else onSelectionChange(immobili.map((m) => m.ubicazione_id));
  };

  const handleToggleInclude = (uid, included) => {
    if (included) {
      if (!selectedIds.includes(uid)) onSelectionChange([...selectedIds, uid]);
    } else {
      onSelectionChange(selectedIds.filter((id) => id !== uid));
    }
  };

  return (
    <section
      data-testid={`garanzia-table-${title?.toLowerCase().replace(/\s+/g, "-")}`}
      className="rounded-lg p-5 mb-6"
      style={{ background: "#FFF", border: `1px solid ${PALETTE.border}` }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          {Icon && (
            <div className="rounded-lg p-2"
                  style={{ background: PALETTE.cream }}>
              <Icon size={22} color={PALETTE.navy} />
            </div>
          )}
          <div>
            <h2
              className="text-lg font-semibold"
              style={{ color: PALETTE.navyDark }}
              data-testid="garanzia-title"
            >
              {title}
            </h2>
            {subtitle && (
              <p className="text-sm mt-1" style={{ color: PALETTE.muted }}>
                {subtitle}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          data-testid="select-all-immobili"
          onClick={toggleAll}
          className="text-xs px-3 py-1.5 rounded border"
          style={{
            borderColor: PALETTE.gold,
            color: PALETTE.navyDark,
            background: allSelected ? PALETTE.goldSoft : "#FFF",
          }}
        >
          {allSelected ? "Deseleziona tutti" : selectAllLabel}
        </button>
      </div>

      {rdWarning && (
        <div
          data-testid="rd-warning"
          className="rounded p-3 mb-4 flex items-start gap-2 text-sm"
          style={{
            background: "#FFEFEF", border: `1px solid ${PALETTE.red}`,
            color: PALETTE.red,
          }}
        >
          <AlertTriangle size={18} />
          <span><strong>R.D.</strong> {rdWarning}</span>
        </div>
      )}

      {immobili.length === 0 ? (
        <div
          className="text-sm py-6 text-center"
          style={{ color: PALETTE.muted }}
        >
          {emptyHint || "Nessun immobile disponibile (carica prima l'anagrafica)."}
        </div>
      ) : (
        <div
          className="overflow-x-auto rounded"
          style={{ border: `1px solid ${PALETTE.border}` }}
        >
          <table className="w-full text-sm border-collapse">
            <thead style={{ background: PALETTE.cream }}>
              <tr>
                <th className="px-2 py-2 text-left" style={{ width: 36 }}>
                  Incl.
                </th>
                <th className="px-2 py-2 text-left">Immobile</th>
                {columns.map((c) => (
                  <th key={c.key} className="px-2 py-2 text-left text-xs">
                    {c.label}
                    {c.required && (
                      <span style={{ color: PALETTE.red }}> *</span>
                    )}
                    {c.tooltip && (
                      <InfoTooltip
                        title={c.tooltip.title}
                        body={c.tooltip.body}
                        ref={c.tooltip.ref}
                        testid={`tooltip-col-${c.key}`}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {immobili.map((m) => (
                <ImmobileRow
                  key={m.ubicazione_id}
                  immobile={m}
                  included={selectedIds.includes(m.ubicazione_id)}
                  perImmobileValues={perImmobile}
                  columns={columns}
                  onToggleInclude={handleToggleInclude}
                  onValueChange={onValueChange}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {subCoperture.length > 0 && someSelected && (
        <div className="mt-5">
          <div className="flex items-center gap-2 mb-2">
            <Info size={16} color={PALETTE.gold} />
            <h3 className="text-sm font-medium"
                 style={{ color: PALETTE.navyDark }}>
              Sub-coperture {subCopMode === "global"
                ? "(applicate globalmente agli immobili inclusi)"
                : "(applicate per singolo immobile)"}
            </h3>
          </div>
          <div
            className="rounded overflow-x-auto"
            style={{ border: `1px solid ${PALETTE.border}` }}
          >
            <table className="w-full text-sm border-collapse">
              <tbody>
                {subCoperture.map((sub) => (
                  <SubCoperturaRow
                    key={sub.code}
                    sub={sub}
                    subCopMode={subCopMode}
                    value={subCopValues[sub.code]}
                    selectedIds={selectedIds}
                    immobili={immobili}
                    onChange={onSubCopChange}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {someSelected && (
        <div
          className="mt-4 text-xs"
          style={{ color: PALETTE.muted }}
          data-testid="immobili-count-info"
        >
          {selectedIds.length} di {immobili.length} immobili inclusi in
          questa garanzia.
        </div>
      )}
    </section>
  );
}

export { numFmt };
